import React, { useEffect, useMemo, useState } from 'react';
import {
  Collapse,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Stack,
  Link,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  PurchaseOrderStatsBucket,
  PurchaseOrderDeliveryMonthDrilldownResponse,
  PurchaseOrderStatsResponse,
} from '../../types/purchasing';
import {
  fetchPurchaseOrdersByDeliveryMonth,
  fetchPurchaseOrderStats,
  fetchSuppliers,
} from '../../services/purchasingService';
import PurchaseOrderStatsFiltersBar from '../../components/purchasing/stats/PurchaseOrderStatsFiltersBar';
import PurchaseOrderMonthDrilldownDrawer from '../../components/purchasing/stats/PurchaseOrderMonthDrilldownDrawer';
import {
  readPurchaseOrderStatsFilters,
  writePurchaseOrderStatsFilters,
} from '../../utils/urlState';
import { SupplierOption } from '../../types/purchasing';

function formatCurrency(value: number, currency = 'SEK') {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function monthLabel(month: string) {
  // month is YYYY-MM
  if (!/^\d{4}-\d{2}$/.test(month)) return month;
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'short',
  }).format(d);
}

type CashflowRow = {
  month: string;
  inbound: number;
  outbound: number;
  net: number;
  accumulated: number;
};

function TabPanel(props: {
  value: string;
  tab: string;
  children: React.ReactNode;
}) {
  const { value, tab, children } = props;
  if (value !== tab) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
}

export default function PurchaseOrderStatistics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useMemo(
    () => readPurchaseOrderStatsFilters(searchParams),
    [],
  );

  const [bucket, setBucket] = useState<PurchaseOrderStatsBucket>(
    initial.bucket,
  );
  const [season, setSeason] = useState<string | null>(initial.season);
  const [supplierId, setSupplierId] = useState<number | null>(
    initial.supplierId,
  );
  const [deliveryMonthFrom, setDeliveryMonthFrom] = useState<string | null>(
    initial.deliveryMonthFrom,
  );
  const [deliveryMonthTo, setDeliveryMonthTo] = useState<string | null>(
    initial.deliveryMonthTo,
  );
  const [focusMonth, setFocusMonth] = useState<string | null>(
    initial.focusMonth,
  );

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PurchaseOrderStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedDueMonth, setExpandedDueMonth] = useState<string | null>(null);

  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);

  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);
  const [drillRows, setDrillRows] = useState<
    PurchaseOrderDeliveryMonthDrilldownResponse['orders']
  >([]);

  const [tab, setTab] = useState<'overview' | 'payments' | 'cashflow'>(
    'overview',
  );

  useEffect(() => {
    let cancelled = false;
    const loadSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const res = await fetchSuppliers();
        if (!cancelled) setSuppliers(res);
      } catch (e) {
        console.error('Failed to load suppliers:', e);
        if (!cancelled) setSuppliers([]);
      } finally {
        if (!cancelled) setSuppliersLoading(false);
      }
    };
    loadSuppliers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSearchParams(
      writePurchaseOrderStatsFilters(new URLSearchParams(), {
        bucket,
        season,
        supplierId,
        deliveryMonthFrom,
        deliveryMonthTo,
        focusMonth,
      }),
      { replace: true },
    );
  }, [
    bucket,
    season,
    supplierId,
    deliveryMonthFrom,
    deliveryMonthTo,
    focusMonth,
    setSearchParams,
  ]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchPurchaseOrderStats({
          bucket,
          season: season || undefined,
          supplierId: supplierId || undefined,
          deliveryMonthFrom: deliveryMonthFrom || undefined,
          deliveryMonthTo: deliveryMonthTo || undefined,
        });
        if (!cancelled) setStats(result);
      } catch (e) {
        console.error('Failed to load purchase order stats:', e);
        if (!cancelled) setError('Kunde inte ladda statistik.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [bucket, season, supplierId, deliveryMonthFrom, deliveryMonthTo]);

  useEffect(() => {
    let cancelled = false;
    const loadDrilldown = async () => {
      if (!focusMonth) {
        setDrillRows([]);
        setDrillError(null);
        setDrillLoading(false);
        return;
      }
      setDrillLoading(true);
      setDrillError(null);
      try {
        const res = await fetchPurchaseOrdersByDeliveryMonth({
          month: focusMonth,
          bucket,
          season: season || undefined,
          supplierId: supplierId || undefined,
          deliveryMonthFrom: deliveryMonthFrom || undefined,
          deliveryMonthTo: deliveryMonthTo || undefined,
        });
        if (!cancelled) setDrillRows(res.orders);
      } catch (e) {
        console.error('Failed to load delivery-month drilldown:', e);
        if (!cancelled) setDrillError('Kunde inte ladda månadsöversikt.');
      } finally {
        if (!cancelled) setDrillLoading(false);
      }
    };
    loadDrilldown();
    return () => {
      cancelled = true;
    };
  }, [
    focusMonth,
    bucket,
    season,
    supplierId,
    deliveryMonthFrom,
    deliveryMonthTo,
  ]);

  const currency = 'SEK';

  const deliverySeries = useMemo(() => {
    const rows = stats?.byDeliveryMonth ?? [];
    return rows.map((r) => ({
      month: r.month,
      totalCost: r.totalCost,
      totalUnits: r.totalUnits,
    }));
  }, [stats]);

  const paymentsSeries = useMemo(() => {
    const rows = stats?.paymentsByDueMonth ?? [];
    return rows.map((r) => ({
      month: r.month,
      dueAmount: r.dueAmount,
    }));
  }, [stats]);

  const dueOrdersByMonth = useMemo(() => {
    const rows = stats?.dueOrders ?? [];
    const map = new Map<string, typeof rows>();
    rows.forEach((r) => {
      const list = map.get(r.month) || [];
      list.push(r);
      map.set(r.month, list);
    });
    return map;
  }, [stats]);

  const topBrands = useMemo(() => stats?.topBrands ?? [], [stats]);

  const cashflow = useMemo<CashflowRow[]>(() => {
    const inbound = new Map<string, number>();
    (stats?.byDeliveryMonth ?? []).forEach((r) => {
      inbound.set(r.month, (inbound.get(r.month) || 0) + (r.totalCost || 0));
    });

    const outbound = new Map<string, number>();
    (stats?.paymentsByDueMonth ?? []).forEach((r) => {
      outbound.set(r.month, (outbound.get(r.month) || 0) + (r.dueAmount || 0));
    });

    const months = Array.from(
      new Set([...Array.from(inbound.keys()), ...Array.from(outbound.keys())]),
    ).sort();

    let acc = 0;
    return months.map((m) => {
      const inb = inbound.get(m) || 0;
      const outb = outbound.get(m) || 0;
      const net = inb - outb;
      acc += net;
      return {
        month: m,
        inbound: inb,
        outbound: outb,
        net,
        accumulated: acc,
      };
    });
  }, [stats]);

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h4">Statistik</Typography>
        <Typography variant="body2" color="text.secondary">
          Inköpsordrar – utkast och färdigmarkerade
        </Typography>
      </Box>

      <PurchaseOrderStatsFiltersBar
        bucket={bucket}
        onBucketChange={setBucket}
        season={season}
        onSeasonChange={setSeason}
        suppliers={suppliers}
        supplierId={supplierId}
        onSupplierIdChange={setSupplierId}
        deliveryMonthFrom={deliveryMonthFrom}
        onDeliveryMonthFromChange={setDeliveryMonthFrom}
        deliveryMonthTo={deliveryMonthTo}
        onDeliveryMonthToChange={setDeliveryMonthTo}
        onReset={() => {
          setSeason(null);
          setSupplierId(null);
          setDeliveryMonthFrom(null);
          setDeliveryMonthTo(null);
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : !stats ? (
        <Paper sx={{ p: 3 }}>
          <Typography>Ingen data.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          <Paper sx={{ p: 0, borderRadius: 3, overflow: 'hidden' }}>
            <Tabs
              value={tab}
              onChange={(_, next) => setTab(next)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Tab value="overview" label="Översikt" />
              <Tab value="payments" label="Betalningar" />
              <Tab value="cashflow" label="Kassaflöde" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              <TabPanel value={tab} tab="overview">
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
                      >
                        Ordrar
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5 }}>
                        {stats.kpis.orderCount}
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
                      >
                        Antal enheter
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5 }}>
                        {stats.kpis.totalUnits.toLocaleString('sv-SE')}
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
                      >
                        Inköpsvärde
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5 }}>
                        {formatCurrency(stats.kpis.totalCost, currency)}
                      </Typography>
                    </Paper>
                  </Box>

                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Inkommande varor per månad
                    </Typography>
                    <Box sx={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={deliverySeries}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                          />
                          <XAxis dataKey="month" tickFormatter={monthLabel} />
                          <YAxis
                            yAxisId="left"
                            tickFormatter={(v) =>
                              formatCurrency(Number(v), currency)
                            }
                          />
                          <YAxis yAxisId="right" orientation="right" />
                          <Tooltip
                            formatter={(value: number, name: string) => {
                              if (name === 'totalCost') {
                                return formatCurrency(Number(value), currency);
                              }
                              return Number(value).toLocaleString('sv-SE');
                            }}
                            labelFormatter={(l) =>
                              `Månad ${monthLabel(String(l))}`
                            }
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="totalCost"
                            name="Inköpsvärde"
                            fill="#7c4dff"
                            onClick={
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              ((data: any) => {
                                const month = data?.payload?.month;
                                if (typeof month === 'string')
                                  setFocusMonth(month);
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              }) as any
                            }
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="totalUnits"
                            name="Antal enheter"
                            stroke="#26a69a"
                            strokeWidth={2}
                            dot={{ r: 3, fill: '#26a69a' }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6">Topp varumärken</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Inköpsvärde
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                      {topBrands.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Ingen data.
                        </Typography>
                      ) : (
                        topBrands.map((b) => (
                          <Box key={b.brandName}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {b.brandName}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {formatCurrency(b.totalCost, currency)}
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                mt: 0.5,
                                height: 6,
                                borderRadius: 10,
                                bgcolor: 'action.hover',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${Math.max(0, Math.min(100, b.pct))}%`,
                                  bgcolor: 'primary.main',
                                }}
                              />
                            </Box>
                          </Box>
                        ))
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              </TabPanel>

              <TabPanel value={tab} tab="payments">
                <Stack spacing={2}>
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Utgående betalningar per månad (förfallo)
                    </Typography>
                    <Box sx={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={paymentsSeries}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                          />
                          <XAxis dataKey="month" tickFormatter={monthLabel} />
                          <YAxis
                            tickFormatter={(v) =>
                              formatCurrency(Number(v), currency)
                            }
                          />
                          <Tooltip
                            formatter={(value: number) =>
                              formatCurrency(Number(value), currency)
                            }
                            labelFormatter={(l) =>
                              `Månad ${monthLabel(String(l))}`
                            }
                          />
                          <Bar dataKey="dueAmount" fill="#ff7043" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    {paymentsSeries.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                          }}
                        >
                          Förfallande ordrar
                        </Typography>
                        <Table size="small" sx={{ mt: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Månad</TableCell>
                              <TableCell align="right">Ordrar</TableCell>
                              <TableCell align="right">Att betala</TableCell>
                              <TableCell align="right" sx={{ width: 56 }}>
                                Visa
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paymentsSeries.map((bucketRow) => {
                              const month = bucketRow.month;
                              const isOpen = expandedDueMonth === month;
                              const orders = dueOrdersByMonth.get(month) || [];

                              return (
                                <React.Fragment key={month}>
                                  <TableRow
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      setExpandedDueMonth(isOpen ? null : month)
                                    }
                                  >
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      {monthLabel(month)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {orders.length.toLocaleString('sv-SE')}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {formatCurrency(
                                        bucketRow.dueAmount,
                                        currency,
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {isOpen ? (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      ) : (
                                        <KeyboardArrowRightIcon fontSize="small" />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={4} sx={{ p: 0 }}>
                                      <Collapse
                                        in={isOpen}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <Box sx={{ px: 2, py: 1.25 }}>
                                          {orders.length === 0 ? (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Inga ordrar.
                                            </Typography>
                                          ) : (
                                            <Stack spacing={0.75}>
                                              {orders.map((o) => (
                                                <Stack
                                                  key={`${o.month}-${o.purchaseOrderId}`}
                                                  direction="row"
                                                  alignItems="center"
                                                  justifyContent="space-between"
                                                >
                                                  <Typography variant="caption">
                                                    <Link
                                                      component={RouterLink}
                                                      to={`/purchasing/purchase-orders/view/${o.purchaseOrderId}`}
                                                      underline="hover"
                                                      sx={{
                                                        fontFamily: 'monospace',
                                                        fontWeight: 700,
                                                      }}
                                                    >
                                                      {o.orderNumber}
                                                    </Link>
                                                    {o.supplierName
                                                      ? ` · ${o.supplierName}`
                                                      : ''}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: 600 }}
                                                  >
                                                    {formatCurrency(
                                                      o.dueAmount,
                                                      currency,
                                                    )}
                                                  </Typography>
                                                </Stack>
                                              ))}
                                            </Stack>
                                          )}
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Paper>
                </Stack>
              </TabPanel>

              <TabPanel value={tab} tab="cashflow">
                <Paper sx={{ p: 2 }} variant="outlined">
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Kassaflöde
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Månad</TableCell>
                        <TableCell align="right">Varuvärde in</TableCell>
                        <TableCell align="right">Att betala ut</TableCell>
                        <TableCell align="right">Netto</TableCell>
                        <TableCell align="right">Ack</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cashflow.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>Ingen data.</TableCell>
                        </TableRow>
                      ) : (
                        cashflow.map((r) => (
                          <TableRow key={r.month}>
                            <TableCell>{r.month}</TableCell>
                            <TableCell align="right">
                              {r.inbound > 0
                                ? formatCurrency(r.inbound, currency)
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {r.outbound > 0
                                ? `−${formatCurrency(r.outbound, currency)}`
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {r.net === 0
                                ? '—'
                                : formatCurrency(r.net, currency)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(r.accumulated, currency)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </TabPanel>
            </Box>
          </Paper>
        </Stack>
      )}

      <PurchaseOrderMonthDrilldownDrawer
        open={Boolean(focusMonth)}
        month={focusMonth}
        onClose={() => setFocusMonth(null)}
        currency={currency}
        season={season}
        bucket={bucket}
        loading={drillLoading}
        error={drillError}
        rows={drillRows}
      />
    </Box>
  );
}
